/**
 * 메뉴 레지스트리 - 단일 소스 관리
 * 모든 메뉴 항목과 라우트를 중앙에서 관리
 */
import { COST_PAYMENT_AMOUNT_ENABLED } from '../lib/featureFlags';
import { 
  Home, Package, ClipboardList, Bell, Settings, FileText, 
  Wrench, Users, BarChart3, CheckSquare, Truck, QrCode, 
  Building2, Trash2, Cog, FileCheck, MapPin, Shield,
  AlertTriangle, Activity, Search, Inbox, MessageSquare, Coins
} from 'lucide-react';

/**
 * 사용자 역할 정의
 */
export const USER_ROLES = {
  SYSTEM_ADMIN: 'system_admin',
  MOLD_DEVELOPER: 'mold_developer',
  STAFF: 'staff',
  MAKER: 'maker',
  PLANT: 'plant'
};

/**
 * 대시보드 경로 매핑 (문서 기준 통일)
 */
export const DASHBOARD_ROUTES = {
  // Phase GK: mold_developer/staff 도 system-admin 대시보드 사용 (사용자 명시)
  // '기본 대시보드는 관리자와 동일 / 메뉴 권한만 구분' — 메뉴는 getMenuByRole(user_type) 으로 별도 결정
  [USER_ROLES.SYSTEM_ADMIN]: '/dashboard/system-admin',
  [USER_ROLES.MOLD_DEVELOPER]: '/dashboard/system-admin',
  [USER_ROLES.STAFF]: '/dashboard/system-admin',
  [USER_ROLES.MAKER]: '/dashboard/maker',
  [USER_ROLES.PLANT]: '/dashboard/plant'
};

/**
 * 시스템 관리자 메뉴 구성
 */
export const SYSTEM_ADMIN_MENU = [
  {
    key: 'dashboard',
    label: '대시보드',
    path: '/dashboard/system-admin',
    icon: Home,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    subMenus: []
  },
  {
    key: 'approvals',
    label: '승인함',
    path: '/approval-inbox',
    icon: Inbox,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    badge: 'pendingApprovals',
    subMenus: [
      { key: 'approvals-kit', label: '통합 승인 대기함', path: '/approval-inbox' },
      { key: 'approvals-all', label: '레거시 승인함', path: '/dashboard/system-admin/approvals' },
      { key: 'approvals-kit-admin', label: '승인 플로우 관리', path: '/dashboard/system-admin/approval-kit' },
      { key: 'approvals-checklist', label: '체크리스트 승인 (레거시)', path: '/dashboard/system-admin/approvals?type=checklist' },
      { key: 'approvals-document', label: '문서 배포 승인 (레거시)', path: '/dashboard/system-admin/approvals?type=document' },
      { key: 'approvals-transfer', label: '이관 승인 (레거시)', path: '/dashboard/system-admin/approvals?type=transfer' },
      { key: 'approvals-scrapping', label: '폐기 승인 (레거시)', path: '/dashboard/system-admin/approvals?type=scrapping' }
    ]
  },
  {
    key: 'risk-monitor',
    label: '운영 모니터링',
    path: '/dashboard/system-admin/risk-monitor',
    icon: AlertTriangle,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    subMenus: [
      { key: 'risk-overview', label: '리스크 현황', path: '/dashboard/system-admin/risk-monitor' },
      { key: 'gps-tracking', label: 'GPS 추적', path: '/mold-location-map' },
      { key: 'partner-operations', label: '협력사 운영현황', path: '/dashboard/system-admin/partner-operations' },
      { key: 'asset-audits', label: '대여자산 실사', path: '/asset-audits' },
      { key: 'as-molds', label: 'A/S 금형 대장', path: '/as-molds' },
      { key: 'activity-log', label: '감사 로그', path: '/dashboard/system-admin/audit-logs' }
    ]
  },
  {
    key: 'molds',
    label: '금형 관리',
    path: '/molds',
    icon: Package,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'molds-list', label: '금형 현황', path: '/molds' },
      { key: 'molds-new', label: '금형 등록', path: '/molds/new' },
      { key: 'molds-bulk', label: '일괄 등록', path: '/molds/bulk-upload' },
      { key: 'molds-lifecycle', label: '개발 진행현황', path: '/molds/lifecycle' },
      { key: 'molds-nurturing-overview', label: '금형 육성현황', path: '/mold-development/nurturing-overview' },
      { key: 'molds-history', label: '금형 이력', path: '/mold-history' },
      { key: 'molds-repair-history', label: '수리 현황', path: '/hq/repair-requests' },
      { key: 'molds-qrcode', label: 'QR 코드 조회/출력', path: '/mold-qrcode' },
      // M2: 성형해석 검증 (molding-engine 기반 사양 정합성 검증 도구)
      { key: 'molds-analysis', label: '성형해석 검증', path: '/molding-analysis' },
      // 2026-10-02: 투자비는 최상위 'investment' 그룹으로 분리 — 금형 그룹의 allowedRoles 가
      //   staff 를 포함하지 않아 담당자(staff)가 메뉴로는 들어올 수 없던 문제도 함께 해소된다.
    ]
  },
  // 2026-10-02: 투자비 관리 — 금형 그룹 서브메뉴에서 최상위 그룹으로 분리.
  //   프로젝트·업체·사양·회차 단계·진행단계·부가세를 한 섹션에서 보도록 뷰를 3개로 나눴다.
  {
    key: 'investment',
    label: '투자비 관리',
    path: '/mold-investment',
    icon: Coins,
    // 2026-10-02 사용자 결정: 투자비 도메인은 관리자 전용 (담당자 메뉴에서는 제거)
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    subMenus: [
      { key: 'investment-overview', label: '투자비 현황', path: '/mold-investment' },
      { key: 'investment-installments', label: '지급 회차', path: '/mold-investment?view=installments' },
      { key: 'investment-grid', label: '투자비 편집', path: '/mold-investment?view=grid' },
    ]
  },

  {
    key: 'masters',
    label: '마스터 관리',
    path: '/dashboard/system-admin/masters',
    icon: Settings,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF],
    subMenus: [
      { key: 'masters-document', label: '표준문서 마스터', path: '/standard-document-master' },
      // 2026-11-02 점검관리 마스터 — 점검 가이드 템플릿 (자사 담당자 이상)
      { key: 'masters-inspection', label: '점검관리 마스터', path: '/inspection-master' },
      { key: 'master-data', label: '기초정보 관리', path: '/master-data' },
      // 2026-08-19: 죽은(404) 이미지 URL 정리 (system_admin 라우트 가드)
      { key: 'admin-image-cleanup', label: '이미지 URL 정리', path: '/admin/image-cleanup' }
    ]
  },
  {
    key: 'rules',
    label: '규칙/기준값',
    path: '/dashboard/system-admin/rules',
    icon: Shield,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    subMenus: [
      { key: 'rules-threshold', label: '기준값 관리', path: '/dashboard/system-admin/rules' },
      { key: 'rules-notification', label: '알림 설정', path: '/notification-settings' }
    ]
  },
  {
    key: 'workflow',
    label: '통합관리',
    path: '/workflow',
    icon: Wrench,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'workflow-all', label: '전체 현황', path: '/workflow' },
      { key: 'workflow-repair', label: '수리 관리', path: '/workflow?tab=repair' },
      { key: 'workflow-transfer', label: '이관 관리', path: '/workflow?tab=transfer' },
      { key: 'workflow-scrapping', label: '금형 폐기', path: '/workflow?tab=scrapping' }
    ]
  },
  {
    key: 'users',
    label: '사용자 관리',
    path: '/dashboard/system-admin/users',
    icon: Users,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    subMenus: [
      { key: 'users-internal', label: '사내 사용자', path: '/users/internal' },
      { key: 'users-requests', label: '계정 요청', path: '/user-requests' },
      // 초기 비밀번호(아이디와 동일) 사용 계정 현황 + 변경 요청 알림
      { key: 'users-password-security', label: '초기 비밀번호 현황', path: '/admin/password-security' }
    ]
  },
  {
    key: 'partner-management',
    label: '협력사 관리',
    path: '/partner-management',
    icon: Building2,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'partner-companies', label: '업체 등록/관리', path: '/companies' },
      { key: 'partner-contacts', label: '협력사 담당자', path: '/users/partner' },
      { key: 'partner-injection', label: '사출기 보유현황', path: '/partner-management/injection-machines' },
      { key: 'partner-equipment', label: '장비 보유현황', path: '/partner-management/equipment' },
      { key: 'partner-paintline', label: '도장라인 보유현황', path: '/partner-management/paint-lines' },
      { key: 'partner-finance', label: '협력사 재무현황', path: '/partner-finance' }
    ]
  },
  {
    key: 'reports',
    label: '통계 리포트',
    path: '/reports',
    icon: BarChart3,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'reports-overview', label: '전체 현황', path: '/reports' },
      { key: 'reports-molds', label: '금형 통계', path: '/reports/molds' }
    ]
  },
  {
    key: 'board',
    label: '게시판',
    path: '/board',
    icon: MessageSquare,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF, USER_ROLES.MAKER, USER_ROLES.PLANT]
  },
  {
    key: 'notifications',
    label: '알림',
    path: '/dashboard/system-admin/notifications',
    icon: Bell,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN],
    badge: 'unreadNotifications',
    subMenus: [
      { key: 'notifications-center', label: '알림 센터', path: '/dashboard/system-admin/notifications' },
      { key: 'notifications-list', label: '알림 목록', path: '/alerts' },
      { key: 'notifications-settings', label: '알림 설정', path: '/notification-settings' }
    ]
  }
];

/**
 * 금형개발 담당 메뉴 구성
 */
export const MOLD_DEVELOPER_MENU = [
  {
    key: 'dashboard',
    label: '대시보드',
    path: '/dashboard/mold-developer',
    icon: Home,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: []
  },
  {
    key: 'molds',
    label: '금형 관리',
    path: '/molds',
    icon: Package,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'molds-list', label: '금형 현황', path: '/molds' },
      { key: 'molds-new', label: '금형 등록', path: '/molds/new' },
      { key: 'molds-bulk', label: '일괄 등록', path: '/molds/bulk-upload' },
      { key: 'molds-lifecycle', label: '개발 진행현황', path: '/molds/lifecycle' },
      { key: 'molds-nurturing-overview', label: '금형 육성현황', path: '/mold-development/nurturing-overview' },
      { key: 'molds-locations', label: '금형 위치 관리', path: '/mold-locations' },
      { key: 'molds-history', label: '금형 이력', path: '/mold-history' },
      { key: 'molds-qrcode', label: 'QR 코드 조회/출력', path: '/mold-qrcode' },
      // M2: 성형해석 검증 (molding-engine 기반 사양 정합성 검증 도구)
      { key: 'molds-analysis', label: '성형해석 검증', path: '/molding-analysis' },
      // 2026-10-02: 투자비는 최상위 'investment' 그룹으로 분리 — 금형 그룹의 allowedRoles 가
      //   staff 를 포함하지 않아 담당자(staff)가 메뉴로는 들어올 수 없던 문제도 함께 해소된다.
    ]
  },

  {
    key: 'approvals',
    label: '승인 및 검토',
    path: '/approval-inbox',
    icon: CheckSquare,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    badge: 'pendingApprovals',
    subMenus: [
      { key: 'approvals-kit', label: '통합 승인 대기함', path: '/approval-inbox' },
      { key: 'approvals-inspection', label: '점검 승인 (레거시)', path: '/inspection-approval' }
    ]
  },
  {
    key: 'risk-monitor',
    label: '운영 모니터링',
    path: '/dashboard/system-admin/risk-monitor',
    icon: AlertTriangle,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'risk-overview', label: '리스크 현황', path: '/dashboard/system-admin/risk-monitor' },
      { key: 'gps-tracking', label: 'GPS 추적', path: '/mold-location-map' },
      { key: 'partner-operations', label: '협력사 운영현황', path: '/dashboard/system-admin/partner-operations' },
      { key: 'asset-audits', label: '대여자산 실사', path: '/asset-audits' },
      { key: 'as-molds', label: 'A/S 금형 대장', path: '/as-molds' },
      { key: 'activity-log', label: '감사 로그', path: '/dashboard/system-admin/audit-logs' }
    ]
  },
  {
    key: 'workflow',
    label: '통합관리',
    path: '/workflow',
    icon: Wrench,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'workflow-all', label: '전체 현황', path: '/workflow' },
      { key: 'workflow-repair', label: '수리 관리', path: '/workflow?tab=repair' },
      { key: 'workflow-transfer', label: '이관 관리', path: '/workflow?tab=transfer' },
      { key: 'workflow-scrapping', label: '금형 폐기', path: '/workflow?tab=scrapping' }
    ]
  },
  {
    // 기초정보 관리 — 캠스 담당자 접근 부여 (2026-08-12 사용자 요청).
    // 라우트(/master-data)·백엔드 CUD 는 이미 mold_developer/staff 허용 — 메뉴 노출만 없었음.
    key: 'masters',
    label: '마스터 관리',
    path: '/master-data',
    icon: Settings,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF],
    subMenus: [
      { key: 'masters-inspection', label: '점검관리 마스터', path: '/inspection-master' },
      { key: 'master-data', label: '기초정보 관리', path: '/master-data' }
    ]
  },
  {
    key: 'partner-management',
    label: '협력사 관리',
    path: '/companies',
    icon: Building2,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: [
      { key: 'partner-list', label: '업체 등록/관리', path: '/companies' },
      { key: 'partner-contacts', label: '협력사 담당자', path: '/users/partner' },
      { key: 'partner-equipment', label: '사출기 보유현황', path: '/partner-management/injection-machines' },
      { key: 'partner-general', label: '장비 보유현황', path: '/partner-management/equipment' },
      { key: 'partner-paintline', label: '도장라인 보유현황', path: '/partner-management/paint-lines' },
      { key: 'partner-finance', label: '협력사 재무현황', path: '/partner-finance' }
    ]
  },
  {
    key: 'reports',
    label: '통계 리포트',
    path: '/reports',
    icon: BarChart3,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: []
  },
  {
    key: 'board',
    label: '게시판',
    path: '/board',
    icon: MessageSquare,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF, USER_ROLES.MAKER, USER_ROLES.PLANT]
  },
  {
    key: 'notifications',
    label: '알림',
    path: '/alerts',
    icon: Bell,
    allowedRoles: [USER_ROLES.MOLD_DEVELOPER],
    subMenus: []
  }
];

/**
 * 제작처 메뉴 구성
 */
export const MAKER_MENU = [
  {
    key: 'dashboard',
    label: '대시보드',
    path: '/dashboard/maker',
    icon: Home,
    allowedRoles: [USER_ROLES.MAKER],
    subMenus: []
  },
  {
    key: 'company-profile',
    label: '업체 프로필',
    path: '/company-profile',
    icon: Building2,
    allowedRoles: [USER_ROLES.MAKER],
    subMenus: []
  },
  {
    key: 'molds',
    label: '금형 현황',
    path: '/molds',
    icon: Package,
    allowedRoles: [USER_ROLES.MAKER],
    subMenus: [
      { key: 'molds-list', label: '금형 목록', path: '/molds' },
      { key: 'molds-lifecycle', label: '개발 진행현황', path: '/molds/lifecycle' },
      // 2026-10-13: '금형 육성현황' 제거 — PC 라우트(pcRoutes 'nurturing-overview')와 서버
      //   (routes/moldNurturing.js)가 admin/dev/staff 전용이라 제작처는 눌러도 차단된다.
      //   들어가지지 않는 메뉴를 보여 주지 않는다.
      { key: 'molds-history', label: '금형 이력', path: '/mold-history' },
      { key: 'molds-operation', label: '운영 입력현황', path: '/dashboard/system-admin/partner-operations' },
      // 제작처도 자사 제작 금형에 한해 QR 조회/출력 가능
      { key: 'molds-qrcode', label: 'QR 코드 조회/출력', path: '/mold-qrcode' },
      // SP-1: 협력사 신고 동선은 모바일로 통일 (PC 관리 화면은 본사 전용)
      { key: 'asset-audit-report', label: '보유금형 실사 신고', path: '/mobile/asset-audits' }
    ]
  },
  {
    key: 'repairs',
    label: '수리 관리',
    path: '/maker/repair-requests',
    icon: Wrench,
    allowedRoles: [USER_ROLES.MAKER],
    subMenus: []
  },
  {
    key: 'approvals',
    label: '승인 대기함',
    path: '/approval-inbox',
    icon: Inbox,
    allowedRoles: [USER_ROLES.MAKER],
    badge: 'pendingApprovals',
    subMenus: []
  },
  {
    key: 'board',
    label: '게시판',
    path: '/board',
    icon: MessageSquare,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF, USER_ROLES.MAKER, USER_ROLES.PLANT]
  },
  {
    key: 'notifications',
    label: '알림',
    path: '/alerts',
    icon: Bell,
    allowedRoles: [USER_ROLES.MAKER],
    subMenus: []
  }
];

/**
 * 생산처 메뉴 구성
 */
export const PLANT_MENU = [
  {
    key: 'dashboard',
    label: '대시보드',
    path: '/dashboard/plant',
    icon: Home,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: []
  },
  {
    key: 'company-profile',
    label: '업체 프로필',
    path: '/company-profile',
    icon: Building2,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: []
  },
  {
    key: 'molds',
    label: '금형 현황',
    path: '/molds',
    icon: Package,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: [
      { key: 'molds-list', label: '금형 목록', path: '/molds' },
      { key: 'molds-history', label: '금형 이력', path: '/mold-history' },
      { key: 'molds-operation', label: '운영 입력현황', path: '/dashboard/system-admin/partner-operations' },
      // SP-1: 협력사 신고 동선은 모바일로 통일 (PC 관리 화면은 본사 전용)
      { key: 'asset-audit-report', label: '보유금형 실사 신고', path: '/mobile/asset-audits' }
    ]
  },
  {
    key: 'inspections',
    label: '점검 관리',
    path: '/plant/select-mold/daily-inspection',
    icon: ClipboardList,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: [
      { key: 'inspection-daily', label: '일상점검', path: '/plant/select-mold/daily-inspection' },
      { key: 'inspection-periodic', label: '정기점검', path: '/plant/select-mold/periodic-inspection' },
      { key: 'inspection-humidification', label: '습합점검', path: '/plant/select-mold/humidification' },
      { key: 'inspection-cleaning', label: '세척점검', path: '/plant/select-mold/cleaning' }
    ]
  },
  {
    key: 'repairs',
    label: '수리 요청',
    path: '/plant/select-mold/repair-request',
    icon: Wrench,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: []
  },
  {
    key: 'transfers',
    label: '이관 관리',
    path: '/plant/select-mold/transfer',
    icon: Truck,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: []
  },
  {
    key: 'approvals',
    label: '승인 관리',
    path: '/approval-inbox',
    icon: FileCheck,
    allowedRoles: [USER_ROLES.PLANT],
    badge: 'pendingApprovals',
    subMenus: [
      { key: 'approvals-kit', label: '통합 승인 대기함', path: '/approval-inbox' },
      { key: 'approval-status', label: '승인 현황', path: '/dashboard/plant?tab=approvals' },
      { key: 'approval-logs', label: '승인 이력', path: '/dashboard/plant?tab=approval-logs' }
    ]
  },
  {
    key: 'board',
    label: '게시판',
    path: '/board',
    icon: MessageSquare,
    allowedRoles: [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.MOLD_DEVELOPER, USER_ROLES.STAFF, USER_ROLES.MAKER, USER_ROLES.PLANT]
  },
  {
    key: 'notifications',
    label: '알림',
    path: '/alerts',
    icon: Bell,
    allowedRoles: [USER_ROLES.PLANT],
    subMenus: []
  }
];

/**
 * 역할별 메뉴 가져오기
 */
/**
 * 기능 플래그로 가려진 메뉴를 제외한다.
 * 2026-10-13: 투자비 관리 설명회 봉인 — 서버가 403 을 주므로 메뉴를 남기면
 *   눌러도 안내 문구만 뜬다. 플래그를 되돌리면 그대로 복귀한다.
 */
function applyFeatureFlags(menu) {
  if (COST_PAYMENT_AMOUNT_ENABLED) return menu;
  return menu.filter((m) => m.key !== 'investment');
}

export function getMenuByRole(userType) {
  return applyFeatureFlags(_menuByRole(userType));
}

function _menuByRole(userType) {
  switch (userType) {
    case USER_ROLES.SYSTEM_ADMIN:
      return SYSTEM_ADMIN_MENU;
    case USER_ROLES.MOLD_DEVELOPER:
      return MOLD_DEVELOPER_MENU;
    case USER_ROLES.STAFF:
      return MOLD_DEVELOPER_MENU;
    case USER_ROLES.MAKER:
      return MAKER_MENU;
    case USER_ROLES.PLANT:
      return PLANT_MENU;
    default:
      return [];
  }
}

/**
 * 역할별 대시보드 경로 가져오기
 */
export function getDashboardPath(userType) {
  return DASHBOARD_ROUTES[userType] || '/';
}

/**
 * 헤더 메뉴 (전역 - 모든 역할 공통)
 */
export const HEADER_MENU = {
  search: { key: 'search', label: '검색', icon: Search },
  notifications: { key: 'notifications', label: '알림', icon: Bell, path: '/alerts' },
  settings: { key: 'settings', label: '설정', icon: Settings }
};

/**
 * 등록된 모든 라우트 목록 (검증용)
 */
export const REGISTERED_ROUTES = [
  '/',
  '/dashboard/system-admin',
  '/dashboard/system-admin/approvals',
  '/dashboard/system-admin/risk-monitor',
  '/dashboard/system-admin/notifications',
  '/dashboard/system-admin/rules',
  '/dashboard/system-admin/users',
  '/dashboard/system-admin/audit-logs',
  '/admin/password-security',
  '/partner-finance',
  '/dashboard/mold-developer',
  '/dashboard/maker',
  '/dashboard/plant',
  '/molds',
  '/molds/new',
  '/molds/bulk-upload',
  '/molds/lifecycle',
  '/molds/master',
  '/mold-history',
  '/mold-qrcode',
  '/molding-analysis',
  '/mold-investment',
  '/checklist/daily',
  '/inspection/periodic',
  '/standard-document-master',
  '/inspection-master',
  '/master-data',
  '/repairs',
  '/hq/repair-requests',
  '/maker/repair-requests',
  '/maker/mold',
  '/company-profile',
  '/plant/select-mold',
  '/repair-request-form',
  '/transfers',
  '/transfers/new',
  '/maintenance',
  '/scrapping',
  '/workflow',
  '/companies',
  '/users/internal',
  '/users/partner',
  '/user-requests',
  '/reports',
  '/alerts',
  '/notification-settings',
  '/inspection-approval',
  '/qr-sessions',
  '/mold-location-map'
];

/**
 * 라우트 존재 여부 검증
 */
export function isRouteRegistered(path) {
  const basePath = path.split('?')[0];
  return REGISTERED_ROUTES.some(route => 
    basePath === route || basePath.startsWith(route + '/')
  );
}
